import React, { useState, useMemo } from "react";

/*
  NPAHome.jsx
  Mobile-first, responsive, dark-background React component (Tailwind CSS required).
  Single-file implementation with components grouped below for convenience.

  How to use:
  - Place this file under your React project's components (e.g. src/pages/NPAHome.jsx)
  - Ensure Tailwind CSS is configured and dark background works (Tailwind classes are used)
  - Replace dummyData and handler stubs with API calls when integrating the backend
  - If file becomes large, split components to /msme/components as noted in comments
*/

// -------------------- Dummy Data (replace with API integration) --------------------
const DUMMY_MSMES = [
  {
    id: "MSME-001",
    name: "Anita Labels Pvt Ltd",
    owner: "Anita Sharma",
    state: "Maharashtra",
    district: "Pune",
    cluster: "Labeling",
    bank: "State Bank of India",
    sakshamScore: 78,
    processMaturity: 64,
    selfDiscipline: 82,
    fundingApplications: [
      { id: "APP-1001", product: "Working Capital", amount: 500000, status: "Pending", progress: 40 },
      { id: "APP-1002", product: "Machinery Loan", amount: 2000000, status: "Approved", progress: 100 },
    ],
    sakshamCertificate: { status: "Valid", expires: "2026-05-01" },
    tags: ["GST-registered", "ISO 9001"],
    isNPA: false,
  },
  {
    id: "MSME-002",
    name: "Kumar Food Processing",
    owner: "Ravi Kumar",
    state: "Uttar Pradesh",
    district: "Lucknow",
    cluster: "Food Processing",
    bank: "Punjab National Bank",
    sakshamScore: 45,
    processMaturity: 28,
    selfDiscipline: 39,
    fundingApplications: [
      { id: "APP-2001", product: "Term Loan", amount: 1500000, status: "Rejected", progress: 100 },
    ],
    sakshamCertificate: { status: "Expired", expires: "2023-10-10" },
    tags: ["UGC"],
    isNPA: true,
  },
  {
    id: "MSME-003",
    name: "Saini Engineering",
    owner: "Prakash Saini",
    state: "Rajasthan",
    district: "Jaipur",
    cluster: "Precision Components",
    bank: "HDFC",
    sakshamScore: 62,
    processMaturity: 55,
    selfDiscipline: 60,
    fundingApplications: [],
    sakshamCertificate: { status: "Pending", expires: null },
    tags: ["MSME-Registered"],
    isNPA: false,
  },
];

const DUMMY_LENDERS = [
  { id: "L-001", name: "State Bank of India" },
  { id: "L-002", name: "HDFC" },
  { id: "L-003", name: "Punjab National Bank" },
];

const DUMMY_ADVISORS = [
  { id: "A-01", name: "Think5Whys Advisor Team" },
];

const ROLE_ENUM = {
  MSME: "MSME",
  LENDER: "LENDER",
  ARC: "ARC",
  MINISTRY: "MINISTRY",
  ADVISOR: "ADVISOR",
};

// -------------------- UI Helpers --------------------
const Icon = ({ name, className = "w-4 h-4" }) => {
  // minimal icon abstraction — replace with lucide/react or fontawesome imports
  const map = {
    search: (
      <svg viewBox="0 0 24 24" fill="none" className={className}>
        <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="11" cy="11" r="6" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    ),
  };
  return map[name] || null;
};

const Card = ({ children, className = "" }) => (
  <div className={`bg-gray-900/40 border border-gray-800 rounded-2xl p-3 ${className}`}>{children}</div>
);

const Badge = ({ children, className = "" }) => (
  <span className={`text-xs px-2 py-1 rounded-full bg-gray-800/60 border border-gray-700 ${className}`}>{children}</span>
);

const Progress = ({ value = 0, height = 8 }) => (
  <div className="w-full bg-gray-800 rounded-full" style={{ height }}>
    <div className="bg-emerald-400 rounded-full" style={{ width: `${value}%`, height }} />
  </div>
);

// -------------------- Page Component --------------------
export default function MSMENPAHome() {
  const [role, setRole] = useState(ROLE_ENUM.MSME);
  const [msmes, setMsmes] = useState(DUMMY_MSMES);
  const [selectedMsmeId, setSelectedMsmeId] = useState(msmes[0].id);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ state: "", district: "", bank: "" });

  const selectedMsme = useMemo(() => msmes.find((m) => m.id === selectedMsmeId), [msmes, selectedMsmeId]);

  function handleToggleNPA(msmeId, assignee = null, assignToARC = false) {
    // local state change — replace with API call
    setMsmes((prev) =>
      prev.map((m) => {
        if (m.id !== msmeId) return m;
        return { ...m, isNPA: !m.isNPA, npaInfo: { assignedTo: assignee ?? null, assignedToARC: assignToARC } };
      }),
    );
  }

  function handleUpdateApplication(msmeId, appId, patch) {
    setMsmes((prev) =>
      prev.map((m) => {
        if (m.id !== msmeId) return m;
        return { ...m, fundingApplications: m.fundingApplications.map((a) => (a.id === appId ? { ...a, ...patch } : a)) };
      }),
    );
  }

  function filteredMsmes() {
    return msmes.filter((m) => {
      if (search) {
        const s = search.toLowerCase();
        if (!(`${m.name} ${m.owner} ${m.state} ${m.district} ${m.id}`.toLowerCase().includes(s))) return false;
      }
      if (filters.state && m.state !== filters.state) return false;
      if (filters.district && m.district !== filters.district) return false;
      if (filters.bank && m.bank !== filters.bank) return false;
      return true;
    });
  }

  // -------------------- Sub-Views / Components --------------------

function MSMEView({ msme = {}, onApply }) {
  return (
    <Card>
      <div className="space-y-3">
        <h3 className="text-sm font-semibold">Funding Options & Progress</h3>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <div className="text-xs text-gray-400 mb-2">Available Products</div>
            <div className="space-y-2">
              {['Working Capital', 'Term Loan', 'Machinery Loan', 'Overdraft'].map((p) => (
                <div key={p} className="flex items-center justify-between p-2 rounded-lg bg-gray-900/30 border border-gray-800">
                  <div className="text-sm">{p}</div>
                  <button className="text-xs w-16 px-3 py-1 rounded-full bg-emerald-600 text-black" onClick={() => onApply({ product: p })}>Apply</button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="text-xs text-gray-400 mb-2">Your Applications</div>
            <div className="space-y-2">
              {msme.fundingApplications.length === 0 && <div className="text-sm text-gray-400">No active applications</div>}
              {msme.fundingApplications.map((a) => (
                <div key={a.id} className="p-2 rounded-lg bg-gray-900/30 border border-gray-800">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium">{a.product}</div>
                      <div className="text-xs text-gray-400">{a.id} • ₹{a.amount.toLocaleString()}</div>
                    </div>
                    <div className="w-28">
                      <Progress value={a.progress} />
                      <div className="text-xs text-gray-400 text-right mt-1">{a.status}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 mt-2">
          <SmallMetric title="Process Maturity" value={`${msme.processMaturity}%`} progress={msme.processMaturity} />
          <SmallMetric title="Self Discipline" value={`${msme.selfDiscipline}%`} progress={msme.selfDiscipline} />
          <SmallMetric title="SAKSHAM" value={`${msme.sakshamScore}%`} progress={msme.sakshamScore} />
        </div>

      </div>
    </Card>
  );
}

function LenderView({ msme = {}, lenders = [], onUpdateApp, onMarkNPA }) {
  const [comment, setComment] = useState("");
  const [selectedLender, setSelectedLender] = useState(lenders[0]?.id || "");

  return (
    <Card>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Application / Lead</h3>
          <div className="text-xs text-gray-400">Owner: {msme.owner}</div>
        </div>

        <div className="space-y-2">
          {msme.fundingApplications.length === 0 && <div className="text-sm text-gray-400">No funding applications</div>}
          {msme.fundingApplications.map((a) => (
            <div key={a.id} className="p-2 rounded-lg bg-gray-900/30 border border-gray-800">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <div className="text-sm font-medium">{a.product} • ₹{a.amount.toLocaleString()}</div>
                  <div className="text-xs text-gray-400">{a.id} • Status: {a.status}</div>
                  <div className="text-xs text-gray-400 mt-2">Progress:</div>
                  <div className="w-full mt-1"><Progress value={a.progress} /></div>
                </div>

                <div className="flex flex-col gap-2 w-44">
                  <select value={selectedLender} onChange={(e)=>setSelectedLender(e.target.value)} className="bg-transparent border border-gray-800 rounded-xl px-2 py-1 text-sm">
                    {lenders.map(l=> <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                  <button className="text-xs px-3 py-2 rounded-full bg-emerald-600 text-black" onClick={()=>onUpdateApp(a.id,{status:'Approved',progress:100})}>Approve</button>
                  <button className="text-xs px-3 py-2 rounded-full bg-amber-500 text-black" onClick={()=>onUpdateApp(a.id,{status:'Conditional',progress:60})}>Conditional</button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="pt-2 border-t border-gray-800">
          <div className="text-xs text-gray-400 mb-1">Post comment / action items for MSME</div>
          <textarea value={comment} onChange={(e)=>setComment(e.target.value)} className="w-full bg-transparent border border-gray-800 rounded-xl px-3 py-2 text-sm placeholder-gray-500" placeholder="Write comments or action items..." />
          <div className="flex gap-2 mt-2">
            <button className="text-xs px-3 py-2 rounded-full bg-emerald-600 text-black">Post</button>
            <button className="text-xs px-3 py-2 rounded-full bg-rose-600 text-black" onClick={()=>onMarkNPA('Think5Whys Advisor')}>Mark as NPA (notify Advisors)</button>
            <button className="text-xs px-3 py-2 rounded-full bg-rose-700 text-black" onClick={()=>onMarkNPA('ARC-Team', true)}>Mark NPA & Assign to ARC</button>
          </div>
        </div>

        <div className="mt-2 text-xs text-gray-400">
          <div>Owner SAKSHAM Certificate: <span className="text-white ml-2">{msme.sakshamCertificate.status}</span></div>
          <div>Process Audit / Maturity: <span className="text-white ml-2">{msme.processMaturity}%</span></div>
        </div>
      </div>
    </Card>
  );
}

function AdvisorView({ msme = {}, advisors = [], onConnect }) {
  return (
    <Card>
      <div className="space-y-3">
        <h3 className="text-sm font-semibold">Think5Whys Advisors Console</h3>
        <div className="text-xs text-gray-400">You can view lender data, MSME scorecards and propose corrective actions.</div>

        <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <div className="text-xs text-gray-400 mb-2">Quick Scorecard</div>
            <div className="space-y-2">
              <div className="flex items-center justify-between"><div className="text-sm">SAKSHAM Score</div><div className="font-medium">{msme.sakshamScore}</div></div>
              <div className="flex items-center justify-between"><div className="text-sm">Process Maturity</div><div className="font-medium">{msme.processMaturity}%</div></div>
              <div className="flex items-center justify-between"><div className="text-sm">Self Discipline</div><div className="font-medium">{msme.selfDiscipline}%</div></div>
            </div>
          </div>

          <div>
            <div className="text-xs text-gray-400 mb-2">Connect / Assign</div>
            <div className="flex flex-col gap-2">
              {advisors.map(a => (
                <div key={a.id} className="flex items-center justify-between p-2 rounded-lg bg-gray-900/30 border border-gray-800">
                  <div>
                    <div className="text-sm">{a.name}</div>
                  </div>
                  <div>
                    <button className="text-xs px-3 py-1 w-32 rounded-full bg-emerald-600 text-black" onClick={()=>onConnect(a.id)}>Connect</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-2 text-xs text-gray-400">Recent lender comments & actions are visible here for advisor to plan corrective & preventive action.</div>
      </div>
    </Card>
  );
}

function ARCView({ msme = {}, onProceed }) {
  return (
    <Card>
      <div className="space-y-3">
        <h3 className="text-sm font-semibold">ARC Console</h3>
        <div className="text-xs text-gray-400">View entire MSME dossier and start recovery/acquisition/liquidation workflows.</div>

        <div className="grid grid-cols-1 gap-3 mt-2">
          <div className="p-2 rounded-lg bg-gray-900/30 border border-gray-800">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">Start Governance / Management Takeover</div>
              <div className="flex gap-2">
                <button className="text-xs px-3 py-2 rounded-full bg-amber-500 text-black" onClick={()=>onProceed('Governance')}>Start</button>
                <button className="text-xs px-3 py-2 rounded-full bg-rose-600 text-black" onClick={()=>onProceed('Liquidation')}>Liquidate</button>
              </div>
            </div>
            <div className="text-xs text-gray-400 mt-2">Use the attached scorecards and process audit to decide next steps.</div>
          </div>

          <div className="p-2 rounded-lg bg-gray-900/30 border border-gray-800">
            <div className="text-sm font-medium">Due Diligence Pack</div>
            <div className="text-xs text-gray-400 mt-2">Download / view financials, contracts, audit findings — (stubbed)</div>
          </div>
        </div>
      </div>
    </Card>
  );
}

function MinistryView({ msmes = [], filters, setFilters }) {
  const [searchTerm, setSearchTerm] = useState("");

  const riskZone = msmes.filter(m => m.sakshamScore < 50 || m.isNPA);

  return (
    <Card>
      <div className="space-y-3">
        <h3 className="text-sm font-semibold">Ministry Dashboard — MSME Risk Overview</h3>
        <div className="text-xs text-gray-400">Search, filter and export lists of MSMEs declared NPA or at-risk.</div>

        <div className="mt-3">
          <div className="flex gap-2">
            <input value={searchTerm} onChange={(e)=>setSearchTerm(e.target.value)} placeholder="Search by name, id, state..." className="flex-1 bg-transparent border border-gray-800 rounded-xl px-3 py-2 text-sm" />
            <button className="text-xs px-3 py-2 w-24 rounded-full bg-emerald-600 text-black">Export CSV</button>
          </div>
        </div>

        <div className="mt-3 max-h-[48vh] overflow-auto">
          <table className="w-full text-sm table-auto">
            <thead className="text-xs text-gray-400 sticky top-0 bg-[#071019]/60">
              <tr>
                <th className="text-left p-2">MSME</th>
                <th className="text-left p-2">State</th>
                <th className="text-left p-2">Bank</th>
                <th className="text-left p-2">Saksham</th>
                <th className="text-left p-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {riskZone.filter(m => `${m.name} ${m.id} ${m.state}`.toLowerCase().includes(searchTerm.toLowerCase())).map(m => (
                <tr key={m.id} className="border-t border-gray-800 hover:bg-gray-900/20">
                  <td className="p-2">{m.name} <div className="text-xs text-gray-400">{m.owner}</div></td>
                  <td className="p-2">{m.state}</td>
                  <td className="p-2">{m.bank}</td>
                  <td className="p-2">{m.sakshamScore}</td>
                  <td className="p-2">{m.isNPA ? <span className="text-rose-400">NPA</span> : <span className="text-amber-400">At-risk</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
}

function SmallMetric({ title, value, progress }) {
  // Note: when splitting to separate file, keep this name or export default
  return (
    <div className="p-2 rounded-lg bg-gray-900/30 border border-gray-800 text-xs">
      <div className="text-xs text-gray-400">{title}</div>
      <div className="text-sm font-semibold mt-1">{value}</div>
      <div className="mt-2">
        <Progress value={progress} />
      </div>
    </div>
  );
}


  // -------------------- Mobile-first layout --------------------
  return (
    <div className="min-h-screen bg-[#0b0f14] text-gray-100 p-4 sm:p-6">
      <header className="flex items-center justify-between mb-4">
        <div className="">
          <h1 className="text-xl font-semibold mb-4">Think5Whys SAKSHAM MSME NPA & Risk Dashboard</h1>
          <div className="flex gap-0">
            {Object.values(ROLE_ENUM).map((r) => (
              <button
                key={r}
                onClick={() => setRole(r)}
                className={`px-3 py-1 rounded-full text-xs ${role === r ? "bg-emerald-600 text-black" : "bg-gray-800/40"}`}>
                {r}
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-12">
        {/* Left column: list / filters */}
        <section className="sm:col-span-4">
          <Card>
            <div className="flex items-center gap-2 mb-3">
              <div className="flex-1">
                <div className="relative">
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search MSME, owner, id..."
                    className="w-full bg-transparent border border-gray-800 rounded-xl px-3 py-2 text-sm placeholder-gray-500"
                  />
                  <div className="absolute right-3 top-2.5 text-gray-400">
                    <Icon name="search" />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-2 mb-3 text-xs">
              <select
                value={filters.state}
                onChange={(e) => setFilters((p) => ({ ...p, state: e.target.value }))}
                className="flex-1 h-10 bg-transparent border border-gray-800 rounded-xl px-2 py-2 text-sm">
                <option value="">All States</option>
                {[...new Set(msmes.map((m) => m.state))].map((st) => (
                  <option key={st} value={st}>{st}</option>
                ))}
              </select>
              <select
                value={filters.bank}
                onChange={(e) => setFilters((p) => ({ ...p, bank: e.target.value }))}
                className="w-36 h-10 bg-transparent border border-gray-800 rounded-xl px-2 py-2 text-sm">
                <option value="">All Banks</option>
                {[...new Set(msmes.map((m) => m.bank))].map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            </div>

            <div className="space-y-2 max-h-[46vh] overflow-y-auto pr-2">
              {filteredMsmes().map((m) => (
                <div
                  key={m.id}
                  onClick={() => setSelectedMsmeId(m.id)}
                  className={`w-full flex items-center justify-between gap-2 p-2 rounded-xl cursor-pointer ${selectedMsmeId === m.id ? "bg-emerald-700/10 border border-emerald-600" : "hover:bg-gray-900/30"}`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center text-sm font-semibold">{m.name.split(" ").slice(0,2).map(s=>s[0]).slice(0,2).join('')}</div>
                      <div>
                        <div className="text-sm font-medium">{m.name}</div>
                        <div className="text-xs text-gray-400">{m.owner} • {m.state}</div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-1">
                    <Badge>{m.isNPA ? "NPA" : "Active"}</Badge>
                    <div className="text-xs text-gray-400">SAKSHAM {m.sakshamScore}</div>
                  </div>
                </div>
              ))}

              {filteredMsmes().length === 0 && <div className="text-sm text-gray-400 p-3">No MSMEs match your filters.</div>}
            </div>
          </Card>
        </section>

        {/* Right column: detail / role-specific panels */}
        <section className="sm:col-span-8">
          <div className="space-y-4">
            {/* Top summary */}
            <Card>
              <div className="flex flex-col items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center text-lg font-bold">
                    {selectedMsme.name.split(" ").slice(0,2).map(s=>s[0]).slice(0,2).join('')}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-lg font-semibold">{selectedMsme.name}</h2>
                      <Badge className="uppercase">{selectedMsme.id}</Badge>
                    </div>
                    <div className="text-xs text-gray-400">{selectedMsme.owner} • {selectedMsme.state}, {selectedMsme.district} • {selectedMsme.cluster}</div>
                  </div>
                </div>

                <div className="flex flex-col items-center gap-2">
                  <div className="text-xs text-gray-400 text-right mr-2">SAKSHAM Score</div>
                  <div className="w-40">
                    <Progress value={selectedMsme.sakshamScore} />
                    <div className="text-xs text-gray-400 mt-1 text-right">{selectedMsme.sakshamScore}%</div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Role specific area */}
            <div>
              {role === ROLE_ENUM.MSME && <MSMEView msme={selectedMsme} onApply={(app)=>console.log('apply',app)} />}
              {role === ROLE_ENUM.LENDER && (
                <LenderView
                  msme={selectedMsme}
                  lenders={DUMMY_LENDERS}
                  onUpdateApp={(appId, patch)=>handleUpdateApplication(selectedMsme.id, appId, patch)}
                  onMarkNPA={(assignTo) => handleToggleNPA(selectedMsme.id, assignTo, false)}
                />
              )}
              {role === ROLE_ENUM.ADVISOR && <AdvisorView msme={selectedMsme} advisors={DUMMY_ADVISORS} onConnect={(a)=>console.log('connect',a)} />}
              {role === ROLE_ENUM.ARC && <ARCView msme={selectedMsme} onProceed={(action)=>console.log('arc action',action)} />}
              {role === ROLE_ENUM.MINISTRY && <MinistryView msmes={msmes} filters={filters} setFilters={setFilters} />}
            </div>

            {/* action footer / quick metrics */}
            <Card>
              <div className="flex items-center justify-between text-xs text-gray-400">
                <div>Last sync: 2025-10-13 21:00 IST</div>
                <div className="flex items-center gap-3">
                  <div>Total MSMEs: <span className="text-white">{msmes.length}</span></div>
                  <div>At-risk: <span className="text-amber-400">{msmes.filter(m=>m.sakshamScore<50).length}</span></div>
                  <div>Declared NPA: <span className="text-rose-400">{msmes.filter(m=>m.isNPA).length}</span></div>
                </div>
              </div>
            </Card>
          </div>
        </section>
      </div>
    </div>
  );
}


